<template>
    <div class="benchmarking-momentum-chart w-100">
        <AonCoverLoading
            :loading="chartLoading || dataLoading"
            :title="'Loading momentum benchmark'"
        />
        <div id="benchmarkingMomentumChart" ref="chartRef"></div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import { config } from '@/config'
import { useViolinChart } from '@/composables/charts/violinChart'
import { dataCobalt } from '@/styles/js_variables.module.scss'

const props = defineProps({
    chartData: {
        type: Array,
        required: true,
        default: () => [],
    },
    competitors: {
        type: Array,
        required: true,
        default: () => [],
    },
    dataLoading: {
        type: Boolean,
        default: false,
    },
})

const chartRef = ref(null)
const chartLoading = ref(true)
let root

// Format competitors for the violin chart with automatically assigned colors
const formattedCompetitors = computed(() => {
    return props.competitors.map((entity) => ({
        displayName: entity.aonEntityName,
        color: dataCobalt,
        id: entity.aonEntityPk,
    }))
})

// Format chart data to match the expected structure for the violin chart
const formattedChartData = computed(() => {
    const validYears = props.chartData.map((item) => item.filingYear).filter((year) => year > 0)

    const recentYears = [...new Set(validYears)].sort((a, b) => b - a).slice(0, 20)

    const result = []

    props.competitors.forEach((competitor) => {
        recentYears.forEach((year) => {
            const existingData = props.chartData.find(
                (item) => item.aonEntityPk === competitor.aonEntityPk && item.filingYear === year
            )

            result.push({
                id: competitor.aonEntityPk,
                aonEntityName: competitor.aonEntityName,
                filingYear: year,
                powerScore: existingData ? existingData.powerScore : 0,
            })
        })
    })

    return result.sort((a, b) => a.filingYear - b.filingYear)
})

onMounted(() => {
    // Initialize amCharts
    am5.addLicense(config.license.AMChartsLicense)
})

watch(
    () => props.chartData,
    async (newVal, oldVal) => {
        await nextTick()
        if (!newVal || newVal.length === 0) {
            return
        }
        chartLoading.value = true
        if (root) {
            root.dispose()
        }
        root = am5.Root.new('benchmarkingMomentumChart')

        const violinChart = useViolinChart(
            root,
            formattedCompetitors.value,
            formattedChartData.value
        )

        violinChart.configureViolin()
        violinChart.createAllSeries({
            scoreLabel: 'Moat Coverage',
        })
        chartLoading.value = false
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    if (root) {
        root.dispose()
    }
})
</script>

<style lang="scss" scoped>
.benchmarking-momentum-chart {
    height: 500px;
    position: relative;

    #benchmarkingMomentumChart {
        width: 100%;
        height: 100%;

        div {
            height: 100%;
        }
    }
}
</style>
